import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Container, MenuItem, Paper, Stack, TextField, Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../network/API';

const REASONS = {
    MANUAL: { label: 'Оценка поставлена вручную', color: 'warning', hint: 'Автопересчёт не трогает оценку, пока не вернуть автообновление.' },
    CLOSED: { label: 'Срок сдачи прошёл', color: 'info', hint: 'Ночной пересчёт для закрытой лекции выключен — только вручную.' },
    NOT_LOWERED: { label: 'Автопересчёт не понижает', color: 'default', hint: 'По решениям получилась меньшая оценка, чем стоит сейчас.' },
    CRITERIA_TEACHER: { label: 'Проверено преподавателем', color: 'secondary', hint: 'ИИ-проверка критериев не перепроверяет такие работы.' },
};

const errText = (e) => (typeof e === 'string' ? e : e?.reason || e?.message || 'Ошибка');

// Все места, где автообновление оценок выключено или не сработает, и причина. Группы грузятся по очереди — список наполняется по мере ответа.
export const AutoGradeIssuesPage = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [groups, setGroups] = useState(null);
    const [data, setData] = useState({});         // groupId -> {groupName, issues}
    const [loadingIds, setLoadingIds] = useState([]);
    const [error, setError] = useState(null);
    const [reason, setReason] = useState('ALL');
    const [query, setQuery] = useState('');
    const [busyKey, setBusyKey] = useState(null);
    const [message, setMessage] = useState(null);
    const groupFilter = params.get('group') || 'ALL';

    const loadGroup = useCallback((id) => {
        setLoadingIds((l) => [...l, id]);
        return ApiService.getAutoGradeIssues(id)
            .then((r) => setData((d) => ({ ...d, [id]: r })))
            .catch((e) => setError(errText(e)))
            .finally(() => setLoadingIds((l) => l.filter((x) => x !== id)));
    }, []);

    useEffect(() => {
        ApiService.getAllGroups().then(setGroups).catch((e) => setError(errText(e)));
    }, []);

    useEffect(() => {
        if (!groups) return;
        const ids = groupFilter === 'ALL' ? groups.map((g) => g.id) : [Number(groupFilter)];
        let cancelled = false;
        (async () => {
            for (const id of ids) {
                if (cancelled) return;
                if (!data[id]) {
                    // eslint-disable-next-line no-await-in-loop
                    await loadGroup(id);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, groupFilter]);

    const all = useMemo(() => {
        const ids = groupFilter === 'ALL' ? Object.keys(data) : [groupFilter];
        return ids.flatMap((id) => (data[id]?.issues || []));
    }, [data, groupFilter]);

    const counts = useMemo(() => {
        const c = {};
        all.forEach((i) => { c[i.reason] = (c[i.reason] || 0) + 1; });
        return c;
    }, [all]);

    const shown = all.filter((i) => (reason === 'ALL' || i.reason === reason)
        && (!query || `${i.studentName} ${i.articleTitle} ${i.tagName} ${i.groupName}`.toLowerCase().includes(query.toLowerCase())));

    const act = async (issue, kind) => {
        const key = `${issue.userId}:${issue.articleId}:${issue.problemId || ''}:${kind}`;
        setBusyKey(key);
        setMessage(null);
        try {
            if (kind === 'reset') await ApiService.resetAutoGrade(issue.userId, issue.articleId);
            else await ApiService.recalculateAutoGrade(issue.userId, issue.articleId);
            setMessage(kind === 'reset' ? 'Автообновление возвращено, оценка пересчитана' : 'Оценка пересчитана');
            await loadGroup(issue.groupId);
        } catch (e) {
            setError(errText(e));
        } finally {
            setBusyKey(null);
        }
    };

    if (!groups) {
        return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Где выключено автообновление оценок</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Здесь все места, где оценка за лекцию не будет пересчитана сама, и почему: оценка поставлена вручную, срок сдачи прошёл, автопересчёт не понижает оценку,
                работа по критериям проверена преподавателем. Для каждой строки можно вернуть автообновление или пересчитать оценку.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <TextField select size="small" label="Группа" value={groupFilter} sx={{ minWidth: 180 }}
                           onChange={(e) => { const v = e.target.value; if (v === 'ALL') params.delete('group'); else params.set('group', v); setParams(params, { replace: true }); }}>
                    <MenuItem value="ALL">Все группы</MenuItem>
                    {groups.map((g) => <MenuItem key={g.id} value={String(g.id)}>{g.name}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Поиск (студент, лекция, предмет)" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: 260 }} />
                {loadingIds.length > 0 && <><CircularProgress size={18} /><Typography variant="body2" color="text.secondary">загружаю группы: {loadingIds.length}</Typography></>}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip label={`Все: ${all.length}`} color={reason === 'ALL' ? 'primary' : 'default'} variant={reason === 'ALL' ? 'filled' : 'outlined'} onClick={() => setReason('ALL')} />
                {Object.entries(REASONS).map(([key, r]) => (
                    <Chip key={key} label={`${r.label}: ${counts[key] || 0}`} color={reason === key ? 'primary' : 'default'} variant={reason === key ? 'filled' : 'outlined'}
                          onClick={() => setReason(reason === key ? 'ALL' : key)} />
                ))}
            </Stack>

            {shown.length === 0 && loadingIds.length === 0 && <Alert severity="success">Таких мест нет: все оценки обновляются автоматически.</Alert>}
            <Stack spacing={1}>
                {shown.map((i) => {
                    const r = REASONS[i.reason] || { label: i.reason, color: 'default' };
                    const key = `${i.userId}:${i.articleId}:${i.problemId || ''}`;
                    return (
                        <Paper key={`${i.groupId}:${key}:${i.reason}`} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                <Chip size="small" label={i.groupName} variant="outlined" onClick={() => navigate(`/groups/${i.groupId}`)} />
                                <Typography sx={{ fontWeight: 600 }}>{i.studentName}</Typography>
                                <Typography variant="body2" color="text.secondary">· {i.tagName} · {i.articleTitle}</Typography>
                                <Box sx={{ flex: 1 }} />
                                <Chip size="small" color={r.color} label={r.label} />
                                {i.storedGrade != null && <Chip size="small" variant="outlined" label={`сейчас ${i.storedGrade}${i.computedGrade != null && i.computedGrade !== i.storedGrade ? `, по решениям ${i.computedGrade}` : ''}`} />}
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>{i.message}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                {i.reason === 'MANUAL' && (
                                    <Button size="small" variant="outlined" disabled={busyKey === `${key}:reset`} onClick={() => act(i, 'reset')}>Вернуть автообновление</Button>
                                )}
                                {(i.reason === 'CLOSED' || i.reason === 'NOT_LOWERED') && (
                                    <Button size="small" variant="outlined" disabled={busyKey === `${key}:recalc`} onClick={() => act(i, 'recalc')}>Пересчитать сейчас</Button>
                                )}
                                <Button size="small" onClick={() => navigate(`/users/${i.userId}`)}>Студент</Button>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        </Container>
    );
};

export default AutoGradeIssuesPage;
